import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Avatar, Button, Popover } from 'antd'
import { LogoutOutlined, SettingOutlined, UserOutlined } from '@ant-design/icons'

import paths from '../../paths'
import MenuSearch from '../MenuSearch'

import './MenuDrawer.less'

interface ScreenSize {
  height: number
  width: number
}

interface UserMenuContentProps {
  onAccessControl: () => void
  onLogout: () => void
}

interface UserSectionProps extends UserMenuContentProps {
  isCompactHeader: boolean
  usuarioNome?: string
}

// Hook personalizado para responsividade
const useResponsive = (): ScreenSize => {
  const [screenSize, setScreenSize] = useState<ScreenSize>({
    width: globalThis.innerWidth,
    height: globalThis.innerHeight,
  })

  useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        width: globalThis.innerWidth,
        height: globalThis.innerHeight,
      })
    }

    // Throttle para otimizar performance
    let timeoutId: ReturnType<typeof setTimeout> | null = null
    const throttledResize = () => {
      if (timeoutId) return

      timeoutId = setTimeout(() => {
        handleResize()
        timeoutId = null
      }, 100)
    }

    globalThis.addEventListener('resize', throttledResize)

    // Cleanup
    return () => {
      globalThis.removeEventListener('resize', throttledResize)
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [])

  return screenSize
}

// Constantes para melhor manutenibilidade
const BREAKPOINT_COMPACT_HEADER = 992
const STORAGE_KEYS = {
  USUARIO_NOME: 'usuario_nome',
}

const COLORS = {
  PRIMARY: '#ec1a23',
  WHITE: '#fff',
  ERROR: '#ff4d4f',
}

// Componente do menu do usuário (fora do render)
const UserMenuContent = ({ onLogout, onAccessControl }: UserMenuContentProps) => (
  <div style={{ minWidth: '200px' }}>
    <Button
      type="text"
      icon={<LogoutOutlined />}
      onClick={onLogout}
      style={{
        width: '100%',
        textAlign: 'left',
        marginBottom: '4px',
      }}
    >
      Sair
    </Button>
    <Button
      type="text"
      icon={<SettingOutlined />}
      onClick={onAccessControl}
      style={{
        width: '100%',
        textAlign: 'left',
      }}
    >
      Visualizar Controle de Acesso
    </Button>
  </div>
)

// Componente da seção do usuário (fora do render)
const UserSection = ({ usuarioNome = '', isCompactHeader, onLogout, onAccessControl }: UserSectionProps) => {
  if (!usuarioNome) return null

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        marginLeft: 'auto',
        gap: '8px',
        flexShrink: 0,
      }}
    >
      {!isCompactHeader && (
        <span
          style={{
            fontWeight: 'bold',
            fontSize: '14px',
            maxWidth: '150px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {usuarioNome}
        </span>
      )}

      <Popover
        content={<UserMenuContent onLogout={onLogout} onAccessControl={onAccessControl} />}
        placement="bottomRight"
        trigger="click"
        overlayClassName="user-menu-popover"
      >
        <Avatar
          icon={<UserOutlined />}
          style={{
            backgroundColor: COLORS.WHITE,
            color: COLORS.ERROR,
            cursor: 'pointer',
            flexShrink: 0,
          }}
          size="default"
        />
      </Popover>
    </div>
  )
}

const MenuDrawer = () => {
  const [usuarioNome, setUsuarioNome] = useState('')
  const navigate = useNavigate()

  // Hook customizado para responsividade
  const { width: screenWidth } = useResponsive()
  const isCompactHeader = screenWidth < BREAKPOINT_COMPACT_HEADER

  // Carrega dados do usuário
  useEffect(() => {
    const nome = localStorage.getItem(STORAGE_KEYS.USUARIO_NOME)
    if (nome) {
      setUsuarioNome(nome)
    }
  }, [])

  // Handlers otimizados com useCallback
  const handleLogout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.USUARIO_NOME)
    navigate('/logout')
  }, [navigate])

  const handleAccessControl = useCallback(() => {
    navigate('/menu-usuario/controle-acesso')
  }, [navigate])

  return (
    <header
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px',
        padding: '0 16px',
        backgroundColor: COLORS.PRIMARY,
        color: COLORS.WHITE,
        height: '64px',
        minHeight: '64px',
      }}
    >
      <MenuSearch
        menuData={paths}
        style={{
          lineHeight: '32px',
          height: '32px',
          display: 'flex',
          alignItems: 'center',
          flex: isCompactHeader ? 1 : '0 1 300px',
          minWidth: 0,
          width: isCompactHeader ? 'auto' : '300px',
        }}
      />

      <UserSection
        usuarioNome={usuarioNome}
        isCompactHeader={isCompactHeader}
        onLogout={handleLogout}
        onAccessControl={handleAccessControl}
      />
    </header>
  )
}

export default MenuDrawer
